import { motion } from 'motion/react'
import { useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'

interface PageTransitionProps {
  children: ReactNode
}

/**
 * Luxe page transition wrapper.
 * Provides smooth fade + slide animations between routes.
 */
export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation()

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (prefersReducedMotion) {
    return <>{children}</>
  }

  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{
        duration: 0.35,
        ease: [0.19, 1, 0.22, 1], // expo.out - snappy with smooth deceleration
      }}
    >
      {children}
    </motion.div>
  )
}

/**
 * Luxe page wrapper for individual pages.
 * Use this to wrap page content for consistent transitions.
 */
export function LuxePage({ children }: PageTransitionProps) {
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (prefersReducedMotion) {
    return <>{children}</>
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.19, 1, 0.22, 1], // expo.out
      }}
    >
      {children}
    </motion.div>
  )
}

export default PageTransition
